import { useState, useEffect } from 'react';
import { X, Plus, Save, AlertTriangle, Globe, Calendar } from 'lucide-react';
import { format, parseISO, addDays, subDays } from 'date-fns';
import { detectSpecialEvent, getTimezoneInfo, localTimeToUTC } from '../utils/dateUtils';

export const SleepEntryForm = ({ 
  isOpen, 
  onClose, 
  onSave, 
  selectedDate, 
  editEntry = null,
  childId,
  childTimezone = null
}) => {
  const [formData, setFormData] = useState({
    startTime: '',
    endTime: '',
    type: 'start',
    comment: '',
    specialEvent: false
  });
  const [errors, setErrors] = useState({});
  const [isSpecialEvent, setIsSpecialEvent] = useState(false);
  const [currentTimezone, setCurrentTimezone] = useState(null);
  const [entryDate, setEntryDate] = useState(selectedDate);

  // Get current time in HH:mm format
  const getCurrentTime = () => {
    const now = new Date();
    return format(now, 'HH:mm');
  };

  useEffect(() => {
    // Get current timezone info
    const timezoneInfo = getTimezoneInfo();
    setCurrentTimezone(timezoneInfo);

    if (editEntry) {
      setFormData({
        startTime: editEntry.startTime ? format(new Date(editEntry.startTime), 'HH:mm') : '',
        endTime: editEntry.endTime ? format(new Date(editEntry.endTime), 'HH:mm') : '',
        type: editEntry.type || 'start',
        comment: editEntry.comment || '',
        specialEvent: editEntry.specialEvent || false
      });
      setIsSpecialEvent(editEntry.specialEvent || false);
      setEntryDate(selectedDate);
    } else {
      // Pre-fill current time for new entries
      const currentTime = getCurrentTime();
      setFormData({
        startTime: currentTime,
        endTime: '',
        type: 'start',
        comment: '',
        specialEvent: false
      });
      setIsSpecialEvent(false);
      setEntryDate(selectedDate);
    }
    setErrors({});
  }, [editEntry, isOpen, selectedDate]);

  useEffect(() => {
    // Auto-detect special events from comment
    if (formData.comment) {
      const detected = detectSpecialEvent(formData.comment);
      setIsSpecialEvent(detected);
      setFormData(prev => ({ ...prev, specialEvent: detected }));
    }
  }, [formData.comment]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.startTime) {
      newErrors.startTime = 'Startzeit ist erforderlich';
    }

    if (formData.type === 'end' && !formData.endTime) {
      newErrors.endTime = 'Endzeit ist erforderlich';
    }

    if (formData.startTime && formData.endTime) {
      const start = new Date(`2000-01-01T${formData.startTime}`);
      const end = new Date(`2000-01-01T${formData.endTime}`);
      
      if (end <= start) {
        newErrors.endTime = 'Endzeit muss nach Startzeit liegen';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    // Convert local times to UTC while preserving the intended local time
    const timezone = childTimezone || currentTimezone?.name || Intl.DateTimeFormat().resolvedOptions().timeZone;
    
    const entryData = {
      childId,
      date: entryDate.toISOString(),
      startTime: formData.startTime ? localTimeToUTC(new Date(`2000-01-01T${formData.startTime}`), timezone).toISOString() : null,
      endTime: formData.endTime ? localTimeToUTC(new Date(`2000-01-01T${formData.endTime}`), timezone).toISOString() : null,
      type: formData.type,
      comment: formData.comment.trim(),
      specialEvent: isSpecialEvent,
      timezone: timezone
    };

    onSave(entryData);
    onClose();
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const handleDateChange = (direction) => {
    if (direction === 'prev') {
      setEntryDate(prev => subDays(prev, 1));
    } else {
      setEntryDate(prev => addDays(prev, 1));
    }
  };

  const setCurrentTime = () => {
    const currentTime = getCurrentTime();
    if (formData.type === 'start') {
      handleInputChange('startTime', currentTime);
    } else if (formData.type === 'end') {
      handleInputChange('endTime', currentTime);
    }
  };

  const setTimeMinus5 = () => {
    const now = new Date();
    const fiveMinutesAgo = new Date(now.getTime() - 5 * 60 * 1000);
    const timeString = format(fiveMinutesAgo, 'HH:mm');
    handleInputChange('endTime', timeString);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              {editEntry ? 'Eintrag bearbeiten' : 'Neuer Schlaf-Eintrag'}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Date Selection */}
            <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
              <button
                type="button"
                onClick={() => handleDateChange('prev')}
                className="p-2 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
              >
                ←
              </button>
              <div className="text-center">
                <p className="text-sm text-gray-600 dark:text-gray-400">Datum</p>
                <p className="font-medium text-gray-900 dark:text-white">
                  {format(entryDate, 'EEEE, d. MMMM yyyy')}
                </p>
              </div>
              <button
                type="button"
                onClick={() => handleDateChange('next')}
                className="p-2 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
              >
                →
              </button>
            </div>

            {/* Timezone Info */}
            {currentTimezone && (
              <div className="flex items-center space-x-2 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                <Globe className="w-4 h-4 text-blue-500" />
                <div className="text-sm text-blue-700 dark:text-blue-300">
                  <span className="font-medium">Zeitzone:</span> {currentTimezone.display}
                </div>
              </div>
            )}

            {/* Entry Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Eintragstyp
              </label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => handleInputChange('type', 'start')}
                  className={`p-3 rounded-lg border-2 transition-colors ${
                    formData.type === 'start'
                      ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300'
                      : 'border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:border-gray-300 dark:hover:border-gray-500'
                  }`}
                >
                  <div className="font-medium">Einschlafen</div>
                  <div className="text-xs opacity-75">Startzeit</div>
                </button>
                <button
                  type="button"
                  onClick={() => handleInputChange('type', 'end')}
                  className={`p-3 rounded-lg border-2 transition-colors ${
                    formData.type === 'end'
                      ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300'
                      : 'border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:border-gray-300 dark:hover:border-gray-500'
                  }`}
                >
                  <div className="font-medium">Aufwachen</div>
                  <div className="text-xs opacity-75">Endzeit</div>
                </button>
              </div>
            </div>

            {/* Time Inputs */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Startzeit
                  </label>
                  <button
                    type="button"
                    onClick={setCurrentTime}
                    className="text-xs text-primary-600 dark:text-primary-400 hover:underline"
                  >
                    Jetzt
                  </button>
                </div>
                <input
                  type="time"
                  value={formData.startTime}
                  onChange={(e) => handleInputChange('startTime', e.target.value)}
                  className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white ${
                    errors.startTime ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.startTime && (
                  <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.startTime}</p>
                )}
              </div>

              {formData.type === 'end' && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Endzeit
                    </label>
                    <div className="flex space-x-2">
                      <button
                        type="button"
                        onClick={setTimeMinus5}
                        className="text-xs text-primary-600 dark:text-primary-400 hover:underline"
                      >
                        -5min
                      </button>
                      <button
                        type="button"
                        onClick={setCurrentTime}
                        className="text-xs text-primary-600 dark:text-primary-400 hover:underline"
                      >
                        Jetzt
                      </button>
                    </div>
                  </div>
                  <input
                    type="time"
                    value={formData.endTime}
                    onChange={(e) => handleInputChange('endTime', e.target.value)}
                    className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white ${
                      errors.endTime ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {errors.endTime && (
                    <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.endTime}</p>
                  )}
                </div>
              )}
            </div>

            {/* Comment Field */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Anmerkungen (optional)
              </label>
              <textarea
                value={formData.comment}
                onChange={(e) => handleInputChange('comment', e.target.value)}
                placeholder="z.B. Unruhige Nacht, Urlaub, Zeitverschiebung, Hochzeit, Jetlag..."
                rows={3}
                className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white resize-none"
              />
            </div>

            {/* Special Event Indicator */}
            {isSpecialEvent && (
              <div className="flex items-center space-x-2 p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800">
                <AlertTriangle className="w-5 h-5 text-yellow-500 flex-shrink-0" />
                <div className="text-sm text-yellow-800 dark:text-yellow-200">
                  <strong>Besonderes Ereignis erkannt:</strong> Dieser Eintrag wird bei der Schlafmuster-Analyse ausgeschlossen.
                </div>
              </div>
            )}

            {/* Manual Special Event Toggle */}
            <div className="flex items-start space-x-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
              <input
                type="checkbox"
                id="specialEvent"
                checked={isSpecialEvent}
                onChange={(e) => {
                  setIsSpecialEvent(e.target.checked);
                  setFormData(prev => ({ ...prev, specialEvent: e.target.checked }));
                }}
                className="mt-1 w-4 h-4 text-primary-600 bg-gray-100 border-gray-300 rounded focus:ring-primary-500 dark:focus:ring-primary-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600 flex-shrink-0"
              />
              <label htmlFor="specialEvent" className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed cursor-pointer">
                Als besonderes Ereignis markieren (wird bei Muster-Analyse ausgeschlossen)
              </label>
            </div>

            {/* Action Buttons */}
            <div className="flex space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="btn-secondary flex-1"
              >
                Abbrechen
              </button>
              <button
                type="submit"
                className="btn-primary flex-1 flex items-center justify-center space-x-2"
              >
                <Save className="w-4 h-4" />
                <span>{editEntry ? 'Aktualisieren' : 'Speichern'}</span>
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}; 